import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { AdminService } from './admin.service';
import { RealtimeService } from './realtime.service';

export interface ControlNotice {
  noticeId: number;
  title: string;
  message: string;
  closeAllowed: boolean;
  blockWork: boolean;
  validFromUtc: string;
  validUntilUtc: string | null;
  updatedAtUtc: string;
  priority: number;
}

export interface ControlPolicy {
  clientId: number | null;
  clientName: string | null;
  notice: ControlNotice | null;
}

@Injectable({ providedIn: 'root' })
export class ClientControlService {
  readonly policy$ = new BehaviorSubject<ControlPolicy>({ clientId: null, clientName: null, notice: null });
  private expiryTimer?: ReturnType<typeof setTimeout>;

  get isAdminRoute(): boolean {
    return window.location.hash.startsWith('#/head-office/client-control');
  }

  constructor(private http: HttpClient, private admin: AdminService, private realtime: RealtimeService) {
    this.realtime.controlPolicyChanged$.subscribe(policy => this.apply(policy));
    this.realtime.connectionChanged$.subscribe(connected => {
      if (connected) void this.load();
      else this.apply(null);
    });
  }

  async load(): Promise<void> {
    try {
      const policy = await firstValueFrom(this.http.get<ControlPolicy>(`${this.admin.BaseURI}/client-control/bootstrap`));
      this.apply(policy);
    } catch {
      this.apply(null); // Head office or client API unavailable: no notice control.
    }
  }

  dismiss(notice: ControlNotice): void {
    if (notice.blockWork) return;
    sessionStorage.setItem(this.readKey(notice), '1');
    this.policy$.next({ ...this.policy$.value, notice: null });
  }

  private apply(policy: ControlPolicy | null): void {
    if (this.expiryTimer) clearTimeout(this.expiryTimer);
    const next = policy ?? { clientId: null, clientName: null, notice: null };
    const notice = next.notice;
    if (notice) {
      const now = Date.now();
      const end = notice.validUntilUtc ? Date.parse(notice.validUntilUtc) : null;
      if (Date.parse(notice.validFromUtc) > now || (end !== null && end <= now) ||
          (!notice.blockWork && sessionStorage.getItem(this.readKey(notice)) === '1')) {
        next.notice = null;
      } else if (end !== null) {
        this.expiryTimer = setTimeout(() => this.apply(null), Math.min(end - now, 2147483647));
      }
    }
    this.policy$.next(next);
  }

  private readKey(notice: ControlNotice): string {
    return `client-control-read-${notice.noticeId}-${notice.updatedAtUtc}`;
  }
}
